import React, { useState, useMemo, useRef, useEffect } from "react";
import { Table, Typography, Spin, Modal, Space, Button, Select, Descriptions, Tooltip } from "antd";
import { InfoCircleOutlined, PlusOutlined, FolderAddOutlined, EditOutlined, DeleteOutlined, WarningOutlined } from "@ant-design/icons";
import ExpandCollapseAllButton from "../../common/ExpandCollapseAllButton";
import NumberInput from "../../common/NumberInput";
import BcCalculationDetails, { computeBcCalculation, SummaryKeyRawFields } from "../../common/BcCalculationDetails";
import { useTender } from "../../../contexts/TenderContext";
import { useDevMode } from "@/contexts/DevModeContext";
import { useCanConfigureBoq } from "@/hooks/useCanConfigureBoq";
import { generateBOQSummaryWithCategories } from "../../../utils/boqSummaryGenerator";
import { CUT_FILL_CATEGORIES } from "../../../utils/billSummaryCategories";
import { COLORS, formatUnit, MAX_SECTION_NESTING_DEPTH, MAX_SECTION_NESTING_DEPTH_MESSAGE, buildSectionDepthMap } from "../../../utils/constants";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import { useGlobalSettings } from "../../../hooks/useGlobalSettings";
import { createCutFillsRateLookupMap, applyCutFillsRatesToSummary } from "../../../utils/cutFillsItemLibraryIntegration";
import { getCutFillConfigureHeadersViolationsByField, type FieldConfigureHeadersViolation } from "../../../utils/groundworksConfigureHeadersValidation";
import { formatDimensionLabel } from "../../../utils/dimensionLabel";
import { useGetRetainingWallsBuildUp } from '../../../hooks/useRetainingWallsBuildUp';
import { useBoqLatestVersion } from '../../../hooks/useBillOfQuantities';
import { BoqSummaryLineItem, computeSoilManagementDerivedCutFillValues } from '../../../utils/groundworksBoqSummaryComputer';
import { getNumericBoqVersion } from '../../../utils/boqVersionPrefix';
import { computeDynamicBcCalculation, type DynamicBillSummaryLineItem } from '../../../utils/dynamicBillSummarySerialization';
import { useBillSummaryExtraItems, buildRootExtraRows, buildNestedExtraRows } from '../../../hooks/useBillSummaryExtraItems';
import { generatePlotMatrixCategoryColumns } from '../../../utils/plotMatrixCategoryGenerator';
import type { DynamicBillSummaryNode, DynamicBillSummaryItemNode, DynamicBillSummarySectionNode } from '../../../types/tender.types';

const findExtraNode = (nodes: DynamicBillSummaryNode[], id: string): DynamicBillSummaryNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.type === "section") {
      const found = findExtraNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

const { Text, Title } = Typography;

// Descriptions matching the column titles in CutFills.tsx exactly
const FIELD_DESCRIPTIONS: Record<string, string> = {
  vegStrip: 'Site strip to remove vegetation',
  topsoilStrip: 'Excavate to strip topsoil, depth n.e. xxmm',
  topsoilDisposalOnsite: 'Topsoil Disposal onsite',
  topsoilDisposalOffsite: 'Topsoil Disposal offsite',
  cutFounds: 'Excavate to reduce level and deposit on site',
  fillFounds: 'Fill to make up levels with site won sub-soil',
  cutPaths: 'Excavate to reduce level and deposit on site',
  fillPaths: 'Fill to make up levels with site won sub-soil',
  cutGarden: 'Excavate to reduce level and deposit on site',
  fillGarden: 'Fill to make up levels with site won sub-soil',
  cutDrives: 'Excavate to reduce level and deposit on site',
  fillDrives: 'Fill to make up levels with site won sub-soil',
  fillToMakeUp6F2: 'Fill to make up levels with 6F2',
};

interface CutFillsSummaryItem {
  key: string;
  itemNumber: string;
  fieldName: string;
  description: string;
  quantity: number;
  unit: string;
  rate: number;
  amount: number;
  category: string;
  resourceCode?: string;
}

const GroundworksCutFillBillSummary: React.FC = () => {
  const showDevMode = useDevMode();
  // Ad-hoc "Add Item"/"Add Section" extra rows — gated by the "Configure BOQ" switch (next to the Re-calculate
  // button on /groundworks/review), independent of Dev Mode (useCanConfigureBoq(), hooks/useCanConfigureBoq.ts).
  // Everything else on this page (SummaryKey column, DB Rate, etc) stays on
  // showDevMode — only the extra-items add/edit/delete controls use this.
  const dynamicSummaryEnabled = useCanConfigureBoq();
  const { state, updateField } = useTender();
  const { settings } = useGlobalSettings();

  const tenderName = state.projectName || '';
  // "currentBoqVersion" can be stored prefixed (e.g. "GWKS-1"/"RS-1" — set by
  // /version-history) — getNumericBoqVersion strips that down to a plain number, same
  // as boqVersionUtils.ts's save-path resolution of retaining-walls build-up specs.
  // Without this, a prefixed string fed straight into useGetRetainingWallsBuildUp's
  // `effectiveVersion >= 1` check coerces to `NaN >= 1` (false), silently disabling the
  // query — retWallsBuildUp stays undefined and the Walls contribution to Subsoil
  // Management's Qty column is understated (missing retaining-wall stockpile net),
  // diverging from the correctly-versioned figure the save path persists.
  const boqVersionNumber = useRef<number>((() => {
    const raw = localStorage.getItem("currentBoqVersion");
    const parsed = raw ? getNumericBoqVersion((JSON.parse(raw) as { version?: number | string })?.version ?? 1) : 1;
    return parsed === 0 ? 1 : parsed;
  })()).current;
  const { data: retWallsBuildUp } = useGetRetainingWallsBuildUp(tenderName, boqVersionNumber, { enabled: !!tenderName });
  const { data: itemLibraryData, isLoading: isLoadingItemLibrary } = useItemLibrary(state.region || "");
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [summaryKeyModalRecord, setSummaryKeyModalRecord] = useState<BoqSummaryLineItem | null>(null);
  const [configViolationModalRecord, setConfigViolationModalRecord] = useState<FieldConfigureHeadersViolation | null>(null);
  const configViolationsByField = useMemo(
    () => getCutFillConfigureHeadersViolationsByField(state.groundworks, retWallsBuildUp?.specifications),
    [state.groundworks, retWallsBuildUp],
  );
  // SummaryKey modal for ad-hoc extra items — these have no persisted boq-versions
  // entry (`apiSummaryItem`) since they aren't serialised into the exported BOQ
  // payload yet, so this shows the live-derived value instead, same "unsaved"
  // convention DynamicBillSummaryPanel's own SummaryKey column uses.
  // `persisted` (when set) is the actual raw `BoqSummaryLineItem` fetched from the
  // BOQ API for this extra item's own `fieldName` — shown in full via
  // `SummaryKeyRawFields` so the modal reflects every detail the API actually
  // has, not just the hand-picked subset of `live`'s fields. `live` (the
  // client-side node, always set) still drives the BCCalculation breakdown,
  // since that derivation needs the node's own `resourceCodes` array — a
  // persisted record only ever carries the combined `resourceCode` string.
  const [extraSummaryKeyRecord, setExtraSummaryKeyRecord] = useState<{
    live: DynamicBillSummaryLineItem;
    persisted: BoqSummaryLineItem | null;
  } | null>(null);

  // Ad-hoc user-added Section/Item rows grafted onto this otherwise-immutable
  // computed table — see useBillSummaryExtraItems.tsx. Gated by
  // dynamicSummaryEnabled (the "Configure BOQ" switch), same as Dynamic Bill
  // Summary. `cutFillsExtraItems` is keyed by "attachment point": `"__root__"`
  // for new top-level rows, or an existing computed row's own `key` for rows
  // grafted onto it.
  const cutFillsExtraItems = state.groundworks?.cutFillsExtraItems;
  // Live Plot Matrix columns, for the Add/Edit Item modal's "Include in Plot
  // Matrix" Valuation Stage lookup — same call GroundworksReview.tsx makes for
  // Dynamic Bill Summary's own equivalent mapping section.
  const extraItemsPlotMatrixColumns = useMemo(
    () => generatePlotMatrixCategoryColumns(state.groundworks as any),
    [state.groundworks],
  );
  // Newly-added extra row (Section or Item) — auto-expands the row it was
  // grafted onto and briefly highlights/autofocuses it, same convention
  // DynamicBillSummaryPanel.tsx follows for its own Add Section/Add Item.
  const [newlyAddedRowId, setNewlyAddedRowId] = useState<string | null>(null);
  useEffect(() => {
    if (!newlyAddedRowId) return;
    const timer = setTimeout(() => setNewlyAddedRowId(null), 2000);
    return () => clearTimeout(timer);
  }, [newlyAddedRowId]);

  const extraItemsApi = useBillSummaryExtraItems({
    extraItems: cutFillsExtraItems,
    onChange: (updated) =>
      updateField("groundworks", { ...state.groundworks, cutFillsExtraItems: updated }),
    itemLibraryData,
    plotMatrixColumns: extraItemsPlotMatrixColumns,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) {
        setExpandedRowKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      }
      setNewlyAddedRowId(nodeId);
    },
  });

  // Dev-mode only: pulls the persisted `groundworks.summary.cutFills` line items from the
  // same boq-versions API (`getBoqVersion`) that drives the rest of this review page, so the
  // SummaryKey column reflects what was actually saved rather than the live recomputation above.
  const { data: latestBoqVersionData } = useBoqLatestVersion(
    state.projectName || undefined,
    { enabled: showDevMode && !!state.projectName, oppNo: state.opportunityNo },
  );

  const apiCutFillsSummaryByFieldName = useMemo(() => {
    const map = new Map<string, BoqSummaryLineItem>();
    const items = (latestBoqVersionData as unknown as { boq?: { billOfQuantities?: Array<{ groundworks?: { summary?: { cutFills?: BoqSummaryLineItem[] } } }> } })
      ?.boq?.billOfQuantities?.[0]?.groundworks?.summary?.cutFills;
    (items || []).forEach((item) => {
      if (item.fieldName) map.set(item.fieldName, item);
    });
    return map;
  }, [latestBoqVersionData]);

  // Create rate lookup map from item library
  const rateLookupMap = useMemo(() => {
    if (!itemLibraryData) {
      return new Map();
    }

    return createCutFillsRateLookupMap(itemLibraryData);
  }, [itemLibraryData]);

  // Generate dynamic summary from actual Cut & Fills data using Item Library rates
  const summaryData = useMemo(() => {
    if (
      !state.groundworks?.cutFillsTable ||
      state.groundworks.cutFillsTable.length === 0
    ) {
      return [];
    }

    // First, generate summary with quantities (rates will be 0)
    const baseItems = generateBOQSummaryWithCategories(
      state.groundworks,
      "Cut & Fills",
      CUT_FILL_CATEGORIES,
      undefined,
      null // No rate mapping - we'll apply rates manually
    );

    // Apply rates from item library lookup map
    const itemsWithRates = applyCutFillsRatesToSummary(baseItems, rateLookupMap);

    // Prefix values for dynamic column titles (e.g. topsoilStrip depth)
    const prefixValues = (state.groundworks as any)?.cutFillsPrefixValues as Record<string, string> | undefined ?? {};

    // Add item numbers and convert to the expected format
    return itemsWithRates.map(
      (item, index) => {
        let description = FIELD_DESCRIPTIONS[item.fieldName] ?? item.description;
        // Apply prefix substitution for topsoilStrip (replaces "xx" with configured depth).
        // Decimal entries (e.g. "0.150") are converted to mm via `formatDimensionLabel`
        // so the bill-summary description always shows millimetre values.
        if (item.fieldName === 'topsoilStrip' && prefixValues['topsoilStrip']) {
          description = description.replace('xx', formatDimensionLabel(prefixValues['topsoilStrip']));
        }
        return {
          key: item.key,
          itemNumber: `${index + 1}.0`,
          fieldName: item.fieldName,
          description,
          quantity: item.quantity,
          unit: item.unit,
          rate: item.rate,
          amount: item.amount,
          category: item.category,
          resourceCode: (item as any).resourceCode || '',
        } as CutFillsSummaryItem;
      }
    );
  }, [state.groundworks, rateLookupMap]);

  // Determine the column type from field name (Founds, Paths, Drives, Garden, POS)
  const getColumnType = (fieldName: string): string => {
    const lowerField = fieldName.toLowerCase();
    if (lowerField.includes("drives") || lowerField.includes("6f2"))
      return "Drives";
    if (lowerField.includes("founds")) return "Founds";
    if (lowerField.includes("paths")) return "Paths";
    if (lowerField.includes("garden")) return "Garden";
    if (lowerField.includes("pos")) return "POS";
    return "Other";
  };

  // Build a code→{rate, description, unit} lookup directly from item library
  const codeRateLookup = useMemo(() => {
    const map = new Map<string, { rate: number; description: string; unit: string }>();
    if (!itemLibraryData) return map;
    itemLibraryData.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) {
        map.set(code, {
          rate: item.nettRate || item.baseRate || 0,
          description: item.resourceDescription || code,
          unit: item.unit || 'm³',
        });
      }
    });
    return map;
  }, [itemLibraryData]);

  // Process data to create nested collapsible grouped structure
  const processedData: any[] = useMemo(() => {
    const markupFactor = (1 + settings.groundworks.overheadAndProfit / 100) * (1 + settings.groundworks.preliminaries / 100);

    const rawProcessedData = summaryData.map((item) => ({
      key: item.key,
      fieldName: item.fieldName,
      description: item.description,
      quantity: item.quantity,
      unit: item.unit,
      rate: item.rate * markupFactor,
      amount: item.amount * markupFactor,
      categoryName: item.category,
      columnType: getColumnType(item.fieldName),
      rowType: "line-item" as const,
      hasData: true,
      resourceCode: item.resourceCode || '',
      dbRate: item.rate.toFixed(4),
      apiSummaryItem: apiCutFillsSummaryByFieldName.get(item.fieldName) || null,
      configViolation: configViolationsByField.get(item.fieldName) ?? null,
    }));

    // Split items into Site Clearance and Cut & Fill categories
    const siteClearanceItems = rawProcessedData.filter(
      (item) => item.categoryName === "Site Clearance"
    );
    const cutFillItems = rawProcessedData.filter(
      (item) => item.categoryName === "Cut & Fill"
    );

    const result: any[] = [];
    let mainSectionIndex = 1; // Dynamic counter for main sections

    // 1. SITE CLEARANCE (dynamic numbering)
    if (siteClearanceItems.length > 0) {
      const sectionNum = mainSectionIndex;
      const siteClearanceParent: any = {
        key: "site-clearance-parent",
        category: `${sectionNum}.0`,
        description: "SITE CLEARANCE",
        rowType: "main-header",
        children: [],
      };

      siteClearanceItems.forEach((item, idx) => {
        const roundedQty = Math.round(item.quantity);
        const displayRate = parseFloat(item.rate.toFixed(2));
        siteClearanceParent.children.push({
          ...item,
          category: `${sectionNum}.${idx + 1}`,
          quantity: String(roundedQty),
          rate: item.rate.toFixed(2),
          amount: Math.abs(roundedQty * displayRate).toFixed(2),
        });
      });

      siteClearanceParent.sectionAmount = siteClearanceParent.children.reduce(
        (sum: number, child: any) => sum + parseFloat(child.amount || '0'),
        0
      );
      result.push(siteClearanceParent);
      mainSectionIndex++; // Increment for next section
    }

    // 2. CUT & FILL (dynamic numbering)
    if (cutFillItems.length > 0) {
      const sectionNum = mainSectionIndex;

      // Group items by column type first
      const columnTypeGroups = new Map<string, any[]>();
      cutFillItems.forEach((item) => {
        if (!columnTypeGroups.has(item.columnType)) {
          columnTypeGroups.set(item.columnType, []);
        }
        columnTypeGroups.get(item.columnType)!.push(item);
      });

      // Create the main CUT & FILL parent
      const cutFillParent: any = {
        key: "cut-fill-parent",
        category: `${sectionNum}.0`,
        description: "CUT & FILL",
        rowType: "main-header",
        children: [],
      };

      // Define the order of column types
      const columnTypeOrder = [
        "Founds",
        "Paths",
        "Garden",
        "POS",
        "Other",
        "Drives",
      ];
      let columnTypeIndex = 1;

      columnTypeOrder.forEach((columnType) => {
        const columnItems = columnTypeGroups.get(columnType);
        if (!columnItems || columnItems.length === 0) return;

        // Create column type parent
        const columnTypeParent: any = {
          key: `col-type-${columnType}`,
          category: `${sectionNum}.${columnTypeIndex}`,
          description: columnType,
          rowType: "column-type-header",
          children: [],
        };

        columnItems.forEach((item, idx) => {
          const roundedQty = Math.round(item.quantity);
          const displayRate = parseFloat(item.rate.toFixed(2));
          columnTypeParent.children.push({
            ...item,
            category: `${sectionNum}.${columnTypeIndex}.${idx + 1}`,
            quantity: String(roundedQty),
            rate: item.rate.toFixed(2),
            amount: Math.abs(roundedQty * displayRate).toFixed(2),
          });
        });

        // Calculate column type total from rounded child amounts
        columnTypeParent.sectionAmount = columnTypeParent.children.reduce(
          (sum: number, child: any) => sum + parseFloat(child.amount || '0'),
          0
        );
        cutFillParent.children.push(columnTypeParent);
        columnTypeIndex++;
      });

      // Calculate total for CUT & FILL parent from column-type children totals
      cutFillParent.sectionAmount = cutFillParent.children.reduce(
        (sum: number, child: any) => sum + (child.sectionAmount || 0),
        0
      );
      result.push(cutFillParent);
      mainSectionIndex++;
    }

    // 3. SOIL MANAGEMENT (Section 3.0)
    {
      const sectionNum = mainSectionIndex;
      const topsoilMgmt = (state.groundworks as any)?.topsoilManagement;
      const subsoilMgmt = (state.groundworks as any)?.subsoilManagement;
      const soilData = (state.groundworks as any)?.soilManagementData;

      // --- 3.1 Topsoil Management ---
      // Qty = |sum(topsoilDisposalOnsite) - sum(topsoilDisposalOffsite)| across cutFillsTable
      const cutFillsTable: any[] = state.groundworks?.cutFillsTable || [];
      const dataRows = cutFillsTable.filter(
        (r) => r.key !== 'totals' && r.plotNo !== 'TOTALS' && r.plotNo !== 'Total'
      );
      const totalOnsite = dataRows.reduce((s, r) => s + (Number(r.topsoilDisposalOnsite) || 0), 0);
      const totalOffsite = dataRows.reduce((s, r) => s + (Number(r.topsoilDisposalOffsite) || 0), 0);
      const topsoilQty = Math.abs(totalOnsite - totalOffsite);

      const topsoilCode = topsoilMgmt?.code?.trim().toUpperCase() || '';
      const topsoilIsExcluded = topsoilCode === 'NONE' || !topsoilCode;
      const topsoilLibrary = topsoilCode && topsoilCode !== 'NONE' ? codeRateLookup.get(topsoilCode) : null;
      const topsoilBaseRate = topsoilLibrary?.rate ?? 0;
      const topsoilRate = topsoilBaseRate * markupFactor;
      // Description comes from Configure Headers sub-option, not item library resourceDescription
      const topsoilDesc = topsoilIsExcluded
        ? (topsoilCode === 'NONE' ? 'Excluded' : 'Not Configured')
        : (topsoilMgmt?.subOption || topsoilCode);
      const topsoilUnit = topsoilLibrary?.unit ?? 'm³';
      const topsoilRoundedQty = Math.round(topsoilQty);
      const topsoilDisplayRate = parseFloat(topsoilRate.toFixed(2));
      const topsoilAmount = topsoilIsExcluded ? 0 : topsoilRoundedQty * topsoilDisplayRate;
      // Matches computeSoilManagementSection's own topsoilFieldName rule (groundworksBoqSummaryComputer.ts)
      // so this row's SummaryKey lookup hits the persisted line it actually corresponds to.
      const topsoilFieldName = (topsoilMgmt?.management === 'Surplus' || !topsoilMgmt?.management)
        ? 'topsoilDisposalOffsite'
        : 'topsoilDisposalOnsite';

      const topsoilSubSection: any = {
        key: 'soil-topsoil-parent',
        category: `${sectionNum}.1`,
        description: 'Topsoil Management',
        rowType: 'column-type-header',
        sectionAmount: parseFloat(topsoilAmount.toFixed(2)),
        children: [
          {
            key: 'soil-topsoil-line',
            category: `${sectionNum}.1.1`,
            description: topsoilDesc,
            quantity: topsoilIsExcluded ? '' : String(topsoilRoundedQty),
            unit: topsoilIsExcluded ? '' : topsoilUnit,
            rate: topsoilIsExcluded ? '' : topsoilDisplayRate.toFixed(2),
            amount: topsoilIsExcluded ? '' : topsoilAmount.toFixed(2),
            rowType: 'line-item',
            fieldName: topsoilFieldName,
            resourceCode: topsoilIsExcluded ? '' : topsoilCode,
            dbRate: topsoilIsExcluded ? '' : topsoilBaseRate.toFixed(4),
            apiSummaryItem: apiCutFillsSummaryByFieldName.get(topsoilFieldName) || null,
            configViolation: configViolationsByField.get(topsoilFieldName) ?? null,
          },
        ],
      };

      // --- 3.2 Subsoil Management ---
      // All 8 Subsoil rows are computed live from their source data via the shared
      // computeSoilManagementDerivedCutFillValues() (groundworksBoqSummaryComputer.ts) —
      // the SAME function computeSoilManagementSection calls when building the persisted
      // SummaryKey payload, so this Qty column and the saved quantity can never drift
      // apart. Only the last 4 rows' FILL values have no live source and stay read from
      // `soilManagementData` directly.
      const computedSoilData: Record<string, number> = {
        ...soilData,
        ...computeSoilManagementDerivedCutFillValues(state.groundworks, retWallsBuildUp?.specifications),
      };
      const soilRows = [
        { cutField: 'foundationsCutArisings', fillField: 'foundationsFill' },
        { cutField: 'slabPathsCut', fillField: 'slabPathsFill' },
        { cutField: 'gardensCut', fillField: 'gardensFill' },
        { cutField: 'drivesCut', fillField: 'drivesFill' },
        { cutField: 'foundationsArisingsCut', fillField: 'foundationsArisingsFill' },
        { cutField: 'plotDrainageCut', fillField: 'plotDrainageFill' },
        { cutField: 'wallsCut', fillField: 'wallsFill' },
        { cutField: 'plotServicesCut', fillField: 'plotServicesFill' },
      ];
      const subsoilTotalCut = soilRows.reduce((s, r) => s + (Number(computedSoilData?.[r.cutField]) || 0), 0);
      const subsoilTotalFill = soilRows.reduce((s, r) => s + (Number(computedSoilData?.[r.fillField]) || 0), 0);
      const subsoilBalance = subsoilTotalCut - subsoilTotalFill;
      const subsoilQty = Math.abs(subsoilBalance);

      const subsoilCode = subsoilMgmt?.code?.trim().toUpperCase() || '';
      const subsoilIsExcluded = subsoilCode === 'NONE' || !subsoilCode;
      const subsoilLibrary = subsoilCode && subsoilCode !== 'NONE' ? codeRateLookup.get(subsoilCode) : null;
      const subsoilBaseRate = subsoilLibrary?.rate ?? 0;
      const subsoilRate = subsoilBaseRate * markupFactor;
      // Description: sub-option + disposal classification suffix for offsite removal
      const subsoilDescBase = subsoilMgmt?.subOption || '';
      const subsoilDisposalClass = subsoilMgmt?.disposalClassification || '';
      const subsoilDescLabel = subsoilDescBase && subsoilDisposalClass
        ? `${subsoilDescBase} – ${subsoilDisposalClass}`
        : subsoilDescBase;
      const subsoilDesc = subsoilIsExcluded
        ? (subsoilCode === 'NONE' ? 'Excluded' : 'Not Configured')
        : (subsoilDescLabel || subsoilCode);
      const subsoilUnit = subsoilLibrary?.unit ?? 'm³';
      const subsoilRoundedQty = Math.round(subsoilQty);
      const subsoilDisplayRate = parseFloat(subsoilRate.toFixed(2));
      const subsoilAmount = subsoilIsExcluded ? 0 : subsoilRoundedQty * subsoilDisplayRate;

      const subsoilSubSection: any = {
        key: 'soil-subsoil-parent',
        category: `${sectionNum}.2`,
        description: 'Subsoil Management',
        rowType: 'column-type-header',
        sectionAmount: parseFloat(subsoilAmount.toFixed(2)),
        children: [
          {
            key: 'soil-subsoil-line',
            category: `${sectionNum}.2.1`,
            description: subsoilDesc,
            quantity: subsoilIsExcluded ? '' : String(subsoilRoundedQty),
            unit: subsoilIsExcluded ? '' : subsoilUnit,
            rate: subsoilIsExcluded ? '' : subsoilDisplayRate.toFixed(2),
            amount: subsoilIsExcluded ? '' : subsoilAmount.toFixed(2),
            rowType: 'line-item',
            fieldName: 'soil-subsoil-line',
            resourceCode: subsoilIsExcluded ? '' : subsoilCode,
            dbRate: subsoilIsExcluded ? '' : subsoilBaseRate.toFixed(4),
            apiSummaryItem: apiCutFillsSummaryByFieldName.get('soil-subsoil-line') || null,
            configViolation: configViolationsByField.get('soil-subsoil-line') ?? null,
          },
        ],
      };

      const soilParent: any = {
        key: 'soil-management-parent',
        category: `${sectionNum}.0`,
        description: 'SOIL MANAGEMENT',
        rowType: 'main-header',
        sectionAmount: parseFloat(topsoilAmount.toFixed(2)) + parseFloat(subsoilAmount.toFixed(2)),
        children: [topsoilSubSection, subsoilSubSection],
      };

      result.push(soilParent);
    }

    // Graft ad-hoc extra rows on top of this otherwise-immutable computed tree —
    // nested additions merge onto the matching existing row (by `key`), and
    // root additions become new top-level sections continuing the same
    // whole-number numbering the 3 fixed sections above use.
    const buckets = cutFillsExtraItems ?? {};
    // Buckets whose attach-point row is actually present in this render's
    // computed tree — anything left over (see orphanedExtraRows below) had
    // its row disappear (e.g. the take-off table was emptied, dropping every
    // column-type-header it was nested under) and must not be silently lost.
    const consumedAttachKeys = new Set<string>();
    const injectExtraRows = (rows: any[]): any[] =>
      rows.map((row) => {
        const bucket = buckets[row.key];
        const existingChildren: any[] = row.children ?? [];
        const ownChildren = row.children ? injectExtraRows(row.children) : undefined;
        if (!bucket || bucket.length === 0) {
          return ownChildren ? { ...row, children: ownChildren } : row;
        }
        consumedAttachKeys.add(row.key);
        const extraRows = buildNestedExtraRows(bucket, row.key, row.category, existingChildren.length, markupFactor);
        const mergedChildren = [...(ownChildren ?? []), ...extraRows];
        return {
          ...row,
          children: mergedChildren,
          ...(row.sectionAmount !== undefined && {
            sectionAmount: mergedChildren.reduce((sum, c) => sum + (parseFloat(c.amount || "0") || 0), 0),
          }),
        };
      });
    const injectedResult = injectExtraRows(result);
    let nextRootNumber = mainSectionIndex + 1;
    const rootExtraRows = buildRootExtraRows(buckets["__root__"] ?? [], "__root__", nextRootNumber, markupFactor);
    nextRootNumber += rootExtraRows.length;
    // Orphaned nested buckets (attach-point row no longer exists — e.g. its
    // parent category/column-type-header disappeared) wrap under a single
    // synthetic "Additional Items" header instead of landing as bare
    // unlabelled rows — not a real node itself (Edit/Delete never targets
    // it, only its real children match a node id), just keyed by the REAL
    // attachKey so those children's Edit/Delete/Qty edits still address the
    // correct bucket in state.
    const orphanedExtraRows = Object.keys(buckets)
      .filter((key) => key !== "__root__" && !consumedAttachKeys.has(key) && buckets[key].length > 0)
      .map((key) => {
        const n = nextRootNumber++;
        const children = buildNestedExtraRows(buckets[key], key, `${n}.0`, 0, markupFactor);
        // This table's attach keys aren't one uniform `prefix-name` scheme —
        // recover the original section title from the small fixed set of
        // keys this file itself ever creates (see `result.push(...)` above),
        // falling back to stripping "col-type-" for a column-type header
        // (whose own `description` is always just the bare column type name).
        const KNOWN_LABELS: Record<string, string> = {
          "site-clearance-parent": "SITE CLEARANCE",
          "cut-fill-parent": "CUT & FILL",
          "soil-management-parent": "SOIL MANAGEMENT",
          "soil-topsoil-parent": "Topsoil Management",
          "soil-subsoil-parent": "Subsoil Management",
        };
        const originalLabel =
          KNOWN_LABELS[key] ?? (key.startsWith("col-type-") ? key.slice("col-type-".length) : "Additional Items");
        return {
          key: `orphaned-${key}`,
          category: `${n}.0`,
          description: originalLabel,
          rowType: "main-header",
          isExtra: true,
          attachKey: key,
          nodeType: "section",
          quantity: "",
          unit: "",
          rate: "",
          amount: "",
          dbRate: "",
          sectionAmount: children.reduce((sum, c) => sum + (parseFloat(c.amount || "0") || 0), 0),
          children,
        };
      });

    return [...injectedResult, ...rootExtraRows, ...orphanedExtraRows];
  }, [summaryData, state.groundworks, codeRateLookup, settings.groundworks.preliminaries, settings.groundworks.overheadAndProfit, retWallsBuildUp, apiCutFillsSummaryByFieldName, cutFillsExtraItems, configViolationsByField]);

  // Depth of every row (real category rows AND grafted extra rows alike) in
  // the merged `processedData` tree — gates a section row's "Add Section"
  // Actions icon at MAX_SECTION_NESTING_DEPTH, counting pre-existing real
  // category nesting as well as already-added extra sections.
  const rowDepthMap = useMemo(() => buildSectionDepthMap(processedData), [processedData]);

  // Show loading state while item library is loading
  if (isLoadingItemLibrary) {
    return (
      <div style={{ padding: "16px", textAlign: "center" }}>
        <Spin size="large" />
        <div style={{ marginTop: "16px" }}>
          <Text className="text-gray-500">Loading item library rates...</Text>
        </div>
      </div>
    );
  }

  // An empty Cut & Fills take-off table (`cutFillsTable`) alone must NOT hide
  // this whole page — user-added extra items (`cutFillsExtraItems`) live
  // independently of the computed take-off data (e.g. grafted onto Soil
  // Management, which always renders, or onto "__root__"), so clearing the
  // take-off table previously made those already-saved extra items vanish
  // from view entirely along with the "no data" early-return, even though
  // they were still present in state and the persisted BOQ.
  const hasExtraItems = Object.values(cutFillsExtraItems ?? {}).some((bucket) => bucket.length > 0);

  return summaryData.length === 0 && !hasExtraItems ? (
    <div style={{ padding: "16px" }}>
      <Text className="text-gray-500">
        No Cut & Fills data available. Please complete the Cut & Fills section
        first.
      </Text>
    </div>
  ) : (
    <div style={{ padding: "16px" }}>
      {/* Header */}
      <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Title
          level={5}
          style={{
            margin: 0,
            fontWeight: 600,
            fontSize: "16px",
            color: "#111827",
          }}
        >
          Cut & Fill Bill Summary
        </Title>
        <Space>
          {dynamicSummaryEnabled && !state.isViewMode && (
            <>
              <Button size="small" icon={<FolderAddOutlined />} onClick={() => extraItemsApi.openAddSection("__root__", null)}>
                Add Section
              </Button>
              {/* Root-level "Add Item" button hidden per request (2026-09-01) — row-level Add Item icons remain.
              <Button size="small" icon={<PlusOutlined />} onClick={() => extraItemsApi.openAddItem("__root__", null)}>
                Add Item
              </Button>
              */}
            </>
          )}
          <ExpandCollapseAllButton
            expanded={expandedRowKeys.length > 0}
            onToggle={() => {
              if (expandedRowKeys.length > 0) {
                setExpandedRowKeys([]);
              } else {
                const allKeys: React.Key[] = [];
                const collect = (rows: any[]) => {
                  rows.forEach((row) => {
                    if (row.children?.length) {
                      allKeys.push(row.key);
                      collect(row.children);
                    }
                  });
                };
                collect(processedData);
                setExpandedRowKeys(allKeys);
              }
            }}
          />
        </Space>
      </div>

      <style>
        {`
            .cutfill-summary-table .ant-table {
              border-radius: 0 !important;
            }
            .cutfill-summary-table .ant-table-container {
              border-radius: 0 !important;
            }
          `}
      </style>
      <Table
        className="cutfill-summary-table"
        dataSource={processedData}
        expandable={{
          childrenColumnName: "children",
          defaultExpandAllRows: false,
          expandedRowKeys: expandedRowKeys,
          onExpand: (expanded, record) => {
            if (expanded) {
              setExpandedRowKeys([...expandedRowKeys, record.key]);
            } else {
              setExpandedRowKeys(
                expandedRowKeys.filter((key) => key !== record.key)
              );
            }
          },
        }}
        columns={[
          {
            title: 'Item',
            dataIndex: 'category',
            key: 'category',
            width: 110,
            align: 'center',
            render: (value, record) => {
              if (record.rowType === 'main-header' || record.rowType === 'sub-header' || record.rowType === 'column-type-header' || record.rowType === 'description-header') {
                return <Text strong>{value}</Text>;
              }
              return value;
            },
          },
          {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: 350,
            render: (value, record) => {
              if (record.rowType === 'main-header') {
                return <Text strong className="text-indigo-600">{value}</Text>;
              }
              if (record.rowType === 'sub-header') {
                return <Text strong className="text-indigo-600">{value}</Text>;
              }
              if (record.rowType === 'column-type-header') {
                return <Text strong className="text-indigo-600">{value}</Text>;
              }
              if (record.rowType === 'description-header') {
                return <Text strong className="text-indigo-600">{value}</Text>;
              }
              if (record.rowType === 'description') {
                return <Text italic className="text-gray-600">{value}</Text>;
              }
              return (
                <div>
                  <Text>{value}</Text>
                  {record.configViolation && (
                    <Tooltip title="Configure Headers required — click for details">
                      <WarningOutlined
                        style={{ color: '#faad14', marginLeft: 6, cursor: 'pointer' }}
                        onClick={() => setConfigViolationModalRecord(record.configViolation)}
                      />
                    </Tooltip>
                  )}
                  {showDevMode && (record.resourceCode || (record.resourceCodes && record.resourceCodes.length > 0)) && (
                    <div style={{ fontSize: 10, color: '#999', fontWeight: 500, marginTop: 2 }}>
                      {record.resourceCode || record.resourceCodes!.map((c: { resourceCode: string; operator: '+' | '-' }, i: number) => (i === 0 ? c.resourceCode : `${c.operator} ${c.resourceCode}`)).join(' ')}
                    </div>
                  )}
                </div>
              );
            },
          },
          {
            title: 'Qty',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 110,
            align: 'right',
            render: (value, record) => {
              if (record.rowType === 'main-header' || record.rowType === 'sub-header' || record.rowType === 'column-type-header' || record.rowType === 'description' || record.rowType === 'description-header') {
                return '';
              }
              // Ad-hoc extra items are directly editable in-cell, same as Dynamic
              // Bill Summary's own Qty column — existing computed rows stay
              // read-only (Qty is edited via the Add/Edit Item modal too, but
              // this is the faster path). Text amount type has no Qty at all,
              // same convention DynamicBillSummaryPanel's Qty column follows.
              if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
                return (
                  <NumberInput
                    value={parseFloat(value) || 0}
                    onChange={(qty) => extraItemsApi.updateNode(record.attachKey, record.key, { qty })}
                    size="small"
                    decimalPlaces={2}
                    autoFocus={record.key === newlyAddedRowId}
                  />
                );
              }
              if (!value || value === '' || value === '0' || value === '0.00' || value === '0.000') return '';
              const numValue = typeof value === 'number' ? value : parseFloat(value);
              return isNaN(numValue) ? '' : <div style={{ textAlign: 'right' }}>{Math.round(numValue).toLocaleString('en-GB')}</div>;
            },
          },
          {
            title: 'Unit',
            dataIndex: 'unit',
            key: 'unit',
            width: 110,
            align: 'center',
            render: (value: string, record) => {
              if (record.rowType === 'main-header' || record.rowType === 'sub-header' || record.rowType === 'column-type-header' || record.rowType === 'description' || record.rowType === 'description-header') {
                return '';
              }
              // Same in-cell Select every Dynamic Bill Summary item row uses.
              // Text amount type has no Unit at all, same convention
              // DynamicBillSummaryPanel's Unit column follows.
              if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
                return (
                  <Select
                    value={value || undefined}
                    onChange={(unit) => extraItemsApi.updateNode(record.attachKey, record.key, { unit })}
                    options={extraItemsApi.unitOptions.map((u) => ({ value: u, label: u }))}
                    placeholder="Unit"
                    size="small"
                    showSearch
                    allowClear
                    style={{ width: "100%" }}
                  />
                );
              }
              return formatUnit(value);
            },
          },
          {
            title: 'Rate',
            dataIndex: 'rate',
            key: 'rate',
            width: 110,
            align: 'right',
            render: (value, record) => {
              if (record.rowType === 'main-header' || record.rowType === 'sub-header' || record.rowType === 'column-type-header' || record.rowType === 'description' || record.rowType === 'description-header') {
                return '';
              }
              if (!value || value === '' || value === '0' || value === '0.00') return '';
              const numValue = typeof value === 'number' ? value : parseFloat(value);
              return isNaN(numValue) ? '' : <div style={{ textAlign: 'right' }}>£{numValue.toFixed(2)}</div>;
            },
          },
          ...(showDevMode ? [{
            title: 'DB Rate',
            dataIndex: 'dbRate',
            key: 'dbRate',
            width: 100,
            align: 'right' as const,
            render: (value: string | number, record: any) => {
              if (record.rowType === 'main-header' || record.rowType === 'sub-header' || record.rowType === 'column-type-header' || record.rowType === 'description-header' || record.rowType === 'description') return '';
              if (!value || value === '') return '';
              const numValue = typeof value === 'number' ? value : parseFloat(value as string);
              if (isNaN(numValue) || numValue === 0) return '';
              return <div style={{ textAlign: 'right', color: '#9ca3af', fontSize: '12px' }}>£{numValue.toFixed(4)}</div>;
            },
          }] : []),
          {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            align: 'right',
            render: (value, record) => {
              if (record.rowType === 'description') {
                return '';
              }

              // Show section total when collapsed for headers with children
              if ((record.rowType === 'main-header' || record.rowType === 'sub-header' || record.rowType === 'column-type-header' || record.rowType === 'description-header') && record.children && record.children.length > 0) {
                const isExpanded = expandedRowKeys.includes(record.key);
                if (!isExpanded && record.sectionAmount > 0) {
                  return (
                    <div style={{ textAlign: 'right' }}>
                      <span className="font-semibold">
                        £{record.sectionAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  );
                }
                return '';
              }

              // Text amount type (extra items only) — a free-typed value like
              // "Included"/"Rate Only" instead of a computed £ amount, same
              // convention DynamicBillSummaryPanel's own Amount column uses.
              if (record.amountType === 'text') {
                return <div style={{ textAlign: 'right' }}><Text>{record.amountText}</Text></div>;
              }

              if (!value || value === "0.00") return '';

              return (
                <div style={{ textAlign: 'right' }}>
                  <Text strong>
                    £{parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Text>
                </div>
              );
            },
          },
          ...(showDevMode ? [{
            title: 'SummaryKey',
            dataIndex: 'apiSummaryItem',
            key: 'summaryKey',
            width: 150,
            align: 'right' as const,
            render: (value: BoqSummaryLineItem | null | undefined, record: any) => {
              if (record.rowType === 'main-header' || record.rowType === 'sub-header' || record.rowType === 'column-type-header' || record.rowType === 'description-header' || record.rowType === 'description') return '';
              if (record.isExtra) {
                const node = findExtraNode(cutFillsExtraItems?.[record.attachKey] ?? [], record.key) as DynamicBillSummaryItemNode | null;
                if (!node) return '';
                const liveAmount = parseFloat(String(record.amount ?? '0')) || 0;
                const lineItem: DynamicBillSummaryLineItem = {
                  key: node.id,
                  description: node.description,
                  quantity: node.qty,
                  unit: node.unit,
                  rate: node.rate,
                  amount: liveAmount,
                  amountType: node.amountType,
                  amountText: node.amountText,
                  resourceCode: node.resourceCode,
                  resourceCodes: node.resourceCodes,
                };
                const bc = computeDynamicBcCalculation(lineItem, (code) => codeRateLookup.get(code)?.rate ?? 0);
                // Once saved, this extra item persists into the real BOQ document's
                // `summary.cutFills` array under its own `key`/`id` (see
                // flattenCutFillsExtraItemsToBoqLines, boqPayloadBuilder.ts) — same
                // persisted-vs-live comparison every other row's SummaryKey shows.
                // Before the first save, there's nothing to compare yet, so this
                // falls back to a live-derived "unsaved" value instead — same
                // convention DynamicBillSummaryPanel's own SummaryKey column uses.
                const persisted = apiCutFillsSummaryByFieldName.get(record.key);
                const persistedAmount = persisted?.amount ?? liveAmount;
                const mismatch = !!persisted && Math.abs(liveAmount - persistedAmount) > 0.01;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : persisted ? '#6b7280' : '#f59e0b' }}>
                        £{persistedAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Text>
                      <InfoCircleOutlined
                        style={{ cursor: 'pointer', color: '#3b82f6' }}
                        onClick={() => setExtraSummaryKeyRecord({ live: lineItem, persisted: persisted ?? null })}
                      />
                    </div>
                    {bc && (
                      <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
                        BC: £{bc.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Text>
                    )}
                    {!persisted && <Text style={{ fontSize: 10, color: '#f59e0b' }}>unsaved</Text>}
                  </div>
                );
              }
              if (!value) return '';
              const bcAmount = computeBcCalculation(value, (code) => codeRateLookup.get(code)?.rate ?? 0).amount;
              const liveAmount = parseFloat(String(record.amount ?? '0')) || 0;
              const mismatch = Math.abs(liveAmount - bcAmount) > 0.01;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : '#6b7280' }}>
                      £{(value.amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Text>
                    <InfoCircleOutlined
                      style={{ cursor: 'pointer', color: '#3b82f6' }}
                      onClick={() => setSummaryKeyModalRecord(value)}
                    />
                  </div>
                  <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
                    BC: £{bcAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Text>
                </div>
              );
            },
          }] : []),
          ...(dynamicSummaryEnabled && !state.isViewMode ? [{
            title: 'Actions',
            key: 'extraActions',
            width: 150,
            align: 'center' as const,
            render: (_: unknown, record: any) => {
              if (record.rowType === 'description') return '';
              if (record.isExtra) {
                const node = findExtraNode(cutFillsExtraItems?.[record.attachKey] ?? [], record.key);
                if (!node) return '';
                if (record.nodeType === 'section') {
                  return (
                    <Space size={4}>
                      <Tooltip title={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
                        <Button
                          size="small"
                          icon={<FolderAddOutlined />}
                          title="Add Section"
                          disabled={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH}
                          onClick={() => extraItemsApi.openAddSection(record.attachKey, record.key)}
                        />
                      </Tooltip>
                      <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.attachKey, record.key)} />
                      <Button size="small" icon={<EditOutlined />} title="Edit Section" onClick={() => extraItemsApi.openEditSection(record.attachKey, node as DynamicBillSummarySectionNode)} />
                      <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey, record.key)} />
                    </Space>
                  );
                }
                return (
                  <Space size={4}>
                    <Button size="small" icon={<EditOutlined />} title="Edit Item" onClick={() => extraItemsApi.openEditItem(record.attachKey, node as DynamicBillSummaryItemNode)} />
                    <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey, record.key)} />
                  </Space>
                );
              }
              // Existing computed row — immutable, but a SECTION row (main-header/
              // column-type-header) can host grafted extra rows. A plain computed
              // line-item gets no Actions at all — only sections are attachment points.
              if (record.rowType !== 'main-header' && record.rowType !== 'sub-header' && record.rowType !== 'column-type-header' && record.rowType !== 'description-header') {
                return '';
              }
              {
                const atMaxDepth = (rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH;
                return (
                  <Space size={4}>
                    <Tooltip title={atMaxDepth ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
                      <Button
                        size="small"
                        icon={<FolderAddOutlined />}
                        title="Add Section"
                        disabled={atMaxDepth}
                        onClick={() => extraItemsApi.openAddSection(record.key, null)}
                      />
                    </Tooltip>
                    <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.key, null)} />
                  </Space>
                );
              }
            },
          }] : []),
        ]}
        pagination={false}
        size="middle"
        bordered
        rowClassName={(record) => {
          const classes: string[] = [];
          if (record.rowType === 'main-header') classes.push('bg-indigo-50', 'font-semibold');
          else if (record.rowType === 'sub-header') classes.push('bg-indigo-50', 'font-semibold');
          else if (record.rowType === 'column-type-header') classes.push('bg-indigo-50', 'font-semibold');
          else if (record.rowType === 'description-header') classes.push('bg-indigo-50', 'font-semibold');
          else if (record.rowType === 'description') classes.push('bg-gray-50');
          if (record.key === newlyAddedRowId) classes.push('cutfill-summary-row-added');
          return classes.join(' ');
        }}
        summary={(pageData) => {
          // Flatten all nested data recursively. A line-item row can now carry
          // extra grafted children (see extraItemsApi/injectExtraRows above) —
          // unlike a pure header row, its own amount still counts even though
          // it also has children, so both branches fire for that case instead
          // of being mutually exclusive.
          const flattenAll = (items: readonly any[]): any[] => {
            let result: any[] = [];
            items.forEach(item => {
              if (item.rowType === 'line-item') {
                result.push(item);
              }
              if (item.children && item.children.length > 0) {
                result.push(...flattenAll(item.children));
              }
            });
            return result;
          };

          const flattenedData = flattenAll(pageData);

          const totalAmt = flattenedData.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

          return (
            <Table.Summary.Row style={{ backgroundColor: "#fafafa", fontWeight: 700 }}>
              <Table.Summary.Cell index={0} colSpan={2}>
                <Text strong style={{ fontWeight: 700 }}>TOTAL CUT & FILL WORKS</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} />
              <Table.Summary.Cell index={3} />
              <Table.Summary.Cell index={4} />
              {showDevMode && <Table.Summary.Cell index={5} />}
              <Table.Summary.Cell index={showDevMode ? 6 : 5} align="right">
                <span style={{ display: 'block', textAlign: 'right', fontWeight: 700 }}>
                  £{totalAmt.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </Table.Summary.Cell>
              {showDevMode && <Table.Summary.Cell index={7} />}
              {dynamicSummaryEnabled && !state.isViewMode && <Table.Summary.Cell index={8} />}
            </Table.Summary.Row>
          );
        }}
      />

      <style>{`
        .cutfill-summary-table .ant-table-thead > tr > th {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
        }
        /* Newly-added extra Section/Item row (see newlyAddedRowId) — a brief
           highlight fade so the row is easy to spot the moment its parent
           auto-expands to reveal it. */
        @keyframes cutfill-summary-row-added-fade {
          0% { background-color: #fef3c7; }
          100% { background-color: transparent; }
        }
        .cutfill-summary-row-added > td {
          animation: cutfill-summary-row-added-fade 1.8s ease-out;
        }
        /* Smooth expand/collapse instead of antd's default instant toggle. */
        .cutfill-summary-table .ant-table-row-expand-icon {
          transition: transform 0.2s ease;
        }
        .cutfill-summary-table .ant-table-tbody > tr {
          transition: background-color 0.3s ease;
        }
      `}</style>

      <Modal
        title="Configure Headers required"
        open={!!configViolationModalRecord}
        onCancel={() => setConfigViolationModalRecord(null)}
        footer={
          <Button type="primary" onClick={() => setConfigViolationModalRecord(null)}>
            OK
          </Button>
        }
      >
        {configViolationModalRecord && (
          <div>
            <Text strong>{configViolationModalRecord.section}</Text>
            <div style={{ marginTop: 8 }}>
              <Text>{configViolationModalRecord.message}</Text>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="SummaryKey — Amount Calculation"
        open={!!summaryKeyModalRecord}
        onCancel={() => setSummaryKeyModalRecord(null)}
        footer={null}
      >
        {summaryKeyModalRecord && (
          <BcCalculationDetails
            record={summaryKeyModalRecord}
            getDbRate={(code) => codeRateLookup.get(code)?.rate ?? 0}
          />
        )}
      </Modal>

      <Modal
        title={extraSummaryKeyRecord?.persisted ? "SummaryKey — Amount Calculation" : "SummaryKey — Live Value (not yet in BOQ export)"}
        open={!!extraSummaryKeyRecord}
        onCancel={() => setExtraSummaryKeyRecord(null)}
        footer={null}
      >
        {extraSummaryKeyRecord && (() => {
          const { live, persisted } = extraSummaryKeyRecord;
          const bc = computeDynamicBcCalculation(live, (code) => codeRateLookup.get(code)?.rate ?? 0);
          return (
            <>
              <SummaryKeyRawFields record={(persisted ?? live) as unknown as Record<string, unknown>} />
              {bc && (
                <div style={{ marginTop: 8, padding: 8, background: '#eff6ff', borderRadius: 4 }}>
                  <Text strong>BCCalculation (backend dbRate-only derivation):</Text>
                  <Descriptions bordered size="small" column={1} style={{ marginTop: 8 }}>
                    <Descriptions.Item label="Computed Rate (rounded 2dp)">£{bc.rate.toFixed(2)}</Descriptions.Item>
                    <Descriptions.Item label="Quantity (rounded)">
                      {bc.quantity.toLocaleString('en-GB')} {formatUnit(live.unit)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Amount (rounded 2dp)">
                      £{bc.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Descriptions.Item>
                  </Descriptions>
                  <div style={{ marginTop: 8 }}>
                    {bc.steps.map((step, idx) => (
                      <div key={idx}>
                        <Text style={{ fontSize: 12, color: '#374151' }}>{step}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </Modal>

      {extraItemsApi.modals}
    </div>
  );
};

export default GroundworksCutFillBillSummary;
